/**
 * TRACED PAGES: one artifact document per page of the brand's own template, copied one to one.
 *
 * ITS OWN KEY ON THE PROFILE (`traces`), NOT `references`, and the four reasons are all in
 * `kitReferences.ts` rather than here:
 *
 *  1. `MAX_REFERENCES` is 28 and `rank()` sorts a role-less record AFTER all 21 roles. `readReferences`
 *     slices at 28 ON READ, so with a full role library only seven traced pages survive a reload. "Trace
 *     the first ten" would store ten and read back seven, silently, and that is exactly the failure mode
 *     this feature was asked to never reproduce.
 *  2. `approvedReference` picks the NEWEST approved record of a family, and a trace is always the newest
 *     thing in the kit. A traced legal appendix stored as an approved reference becomes the exemplar every
 *     future generation of that family copies its composition from.
 *  3. `isReference` requires a real `family`, so a trace would need a fake one invented purely to clear
 *     the guard, and (2) is what that fake one then does.
 *  4. `readReferences`' legacy fallback hands a role-less generated record the role of its family, which
 *     drops a traced page on top of a real composition tile.
 *
 * A page ASSIGNED TO A ROLE is the other half of the feature and it deliberately does NOT come here: it is
 * traced and stored as that role's reference, where the 21 tiles already show it and generation already
 * reaches for it. This store is for the pages that answer no role, which is what "bring the first ten"
 * produces.
 *
 * PAYLOAD MATH, the same argument as the reference library's. A trace is ~7k characters at the median, so
 * twenty is around 140KB riding every profile PATCH and every kit hydrate, on top of the references'
 * 150-450KB. That is the whole reason for {@link MAX_TRACES}, and the reason it is 20 rather than 160.
 *
 * Pure: profile in, values out. No fetch, no DOM.
 */
import type { MoodboardProfile } from '../types/presentation'
import { REFERENCE_MAX_CHARS } from './kitReferences'

/** One page of the template, copied. Identified by its PAGE NUMBER, which is the only stable name it has:
 *  it answers no role and its title is whatever the model read off the page. */
export interface TracedPage {
  /** The page's own number in the source document, 1-based. The record's identity. */
  page: number
  /** A complete artifact document. Never above {@link TRACE_MAX_CHARS}. */
  html: string
  at: number
  /** The manifest title, display only. */
  title?: string
  /** What the page index called this page, if a person had labelled it. */
  label?: string
  /** Which template it came out of, so a sheet can say so after the index has been replaced. */
  fileName?: string
  /** Validator taste flags, what the geometry gate measured, and any honesty note (an unmeasured page,
   *  a stripped mark). Never a reason to refuse the record. */
  flags?: string[]
}

/**
 * The same ceiling one reference gets, and for the same reason: a document that cannot ride a prompt is
 * not worth storing. Imported rather than restated so the two cannot drift apart by an edit to one.
 */
export const TRACE_MAX_CHARS = REFERENCE_MAX_CHARS

/**
 * How many traced pages a kit keeps. See PAYLOAD MATH above.
 *
 * TWENTY IS ALSO THE HONEST ANSWER TO "BRING THE FIRST TEN", with room to ask twice. A run that would
 * exceed it is refused BEFORE it starts, by `traceCapacity`, so nobody watches ten documents write and
 * then finds seven of them gone on reload.
 */
export const MAX_TRACES = 20

export type ProfileWithTraces = MoodboardProfile & { traces?: TracedPage[] }

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

function isTrace(value: unknown): value is TracedPage {
  if (!value || typeof value !== 'object') return false
  const t = value as Partial<TracedPage>
  return (
    typeof t.page === 'number' &&
    Number.isFinite(t.page) &&
    t.page > 0 &&
    typeof t.html === 'string' &&
    t.html.length > 0 &&
    t.html.length <= TRACE_MAX_CHARS &&
    typeof t.at === 'number' &&
    Number.isFinite(t.at)
  )
}

/** Page order, always. A traced set is a document, and a document read out of order is not one. */
const byPage = (a: TracedPage, b: TracedPage): number => a.page - b.page

/**
 * The stored set, read defensively: unknown shapes dropped, one per page (newest wins), in page order.
 *
 * SLICED THE SAME WAY {@link withTraces} SLICES, and that symmetry is the point rather than a detail. The
 * reference library slices on read AND on write, but by a rank that puts fresh records last, so a write of
 * ten and a read of seven are both correct and disagree. Here both ends cut the same tail off the same
 * ordering, so what a write kept is what a read returns. The record that would be lost is refused at the
 * door instead: see {@link traceCapacity}.
 */
export function readTraces(profile: MoodboardProfile | null | undefined): TracedPage[] {
  const raw = (profile as ProfileWithTraces | null | undefined)?.traces
  if (!Array.isArray(raw)) return []
  const byNumber = new Map<number, TracedPage>()
  for (const item of raw) {
    if (!isTrace(item)) continue
    const next: TracedPage = {
      page: Math.floor(item.page),
      html: item.html,
      at: item.at,
      ...(str(item.title) ? { title: str(item.title) } : {}),
      ...(str(item.label) ? { label: str(item.label) } : {}),
      ...(str(item.fileName) ? { fileName: str(item.fileName) } : {}),
      ...(Array.isArray(item.flags) && item.flags.length
        ? { flags: item.flags.filter((f): f is string => typeof f === 'string') }
        : {}),
    }
    const prior = byNumber.get(next.page)
    if (!prior || next.at >= prior.at) byNumber.set(next.page, next)
  }
  return [...byNumber.values()].sort(byPage).slice(0, MAX_TRACES)
}

/** The profile with the set written back; the key is dropped entirely when the set is empty. */
export function withTraces(profile: MoodboardProfile, traces: readonly TracedPage[]): MoodboardProfile {
  const { traces: _drop, ...rest } = profile as ProfileWithTraces
  void _drop
  const kept = [...traces].sort(byPage).slice(0, MAX_TRACES)
  const next: ProfileWithTraces = kept.length ? { ...rest, traces: kept } : { ...rest }
  return next as unknown as MoodboardProfile
}

/** A deck binding snapshots the kit; the traces are the live kit's business, like the references. */
export function stripTraces(profile: MoodboardProfile): MoodboardProfile {
  return withTraces(profile, [])
}

export function upsertTrace(traces: readonly TracedPage[], next: TracedPage): TracedPage[] {
  return [...traces.filter((t) => t.page !== next.page), next].sort(byPage)
}

export function dropTrace(traces: readonly TracedPage[], page: number): TracedPage[] {
  return traces.filter((t) => t.page !== page)
}

/**
 * Why a record would not survive being stored, or null when it would.
 *
 * CHECKED BEFORE THE COMMIT, NEVER AFTER. `readTraces` drops an oversized record silently, which reads to
 * a user as a save that worked and a reload that ate it — the single failure mode this feature was told
 * not to reproduce. So every writer calls this first and says the sentence it hands back.
 */
export function traceRefusal(html: string): string | null {
  if (typeof html !== 'string' || html.length === 0) return 'Nothing came back'
  if (html.length > TRACE_MAX_CHARS) {
    return `Came out too long to keep (${html.length} characters against a ${TRACE_MAX_CHARS} limit)`
  }
  return null
}

/**
 * How many MORE pages this kit can hold, and which of the asked-for pages would not fit.
 *
 * A page already stored costs nothing to trace again: it replaces its own record. So capacity is counted
 * against the pages that are genuinely new, and the answer is a plan the caller can say out loud before
 * anything is spent, rather than a surprise after ten generations.
 */
export function traceCapacity(
  stored: readonly TracedPage[],
  wanted: readonly number[]
): { fits: number[]; overflow: number[]; room: number } {
  const held = new Set(stored.map((t) => t.page))
  const room = Math.max(0, MAX_TRACES - held.size)
  const fits: number[] = []
  const overflow: number[] = []
  let spent = 0
  for (const page of wanted) {
    if (held.has(page)) {
      fits.push(page)
      continue
    }
    if (spent < room) {
      spent += 1
      fits.push(page)
    } else {
      overflow.push(page)
    }
  }
  return { fits, overflow, room }
}
